import React, { useEffect, useRef, useState } from "react";
import { motion } from "framer-motion";

const FREDOKA = { fontFamily: "'Fredoka', sans-serif" };

const stableHash = (str) => {
  let h = 0;
  for (let i = 0; i < (str || "").length; i++) h = (h << 5) - h + str.charCodeAt(i);
  return h;
};

const isProcessingStatus = (status, fileUrl) => {
  const s = String(status || "").toLowerCase();
  // Backend may return ready/completed after auto-finalization — never keep
  // a local "Generating" overlay for those.
  if (s === "ready" || s === "completed" || s === "generated" || s === "complete") {
    return false;
  }
  return (
    s === "processing" ||
    status === "PROCESSING" ||
    status === "requested" ||
    status === "pending" ||
    status === "REQUESTED" ||
    status === "PENDING" ||
    (!fileUrl && status !== "COMPLETED" && status !== "GENERATED")
  );
};

const isFailedStatus = (status) =>
  status === "FAILED" || status === "failed" || status === "ERROR";

/**
 * One picture book — a face-out, tilted, slightly-shadowed cover that
 * stands on the shelf. Used for stories.
 */
const PictureBook = ({ story, index = 0, onClick }) => {
  const [hovered, setHovered] = useState(false);

  const title = story.name || story.title || "Untitled";
  const thumb =
    story.thumbnail_url ||
    story.cover_image_url ||
    "/assets/story-placeholder.png";
  const failed = isFailedStatus(story.status);
  const processing = !failed && isProcessingStatus(story.status, story.file_url);
  const percent = Math.max(0, Math.min(100, Number(story.status_percentage) || 0));
  const isAiVoice = story.creation_method === "ai_voice";

  // Stable lean angle in [-4, 4]° per story so the row looks hand-arranged.
  const lean = (((stableHash(String(story.id || title))) % 9) - 4); // -4..+4

  return (
    <motion.button
      type="button"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={() => !failed && onClick?.(story)}
      // Hover: straighten up and lift; otherwise rest at the lean angle.
      animate={
        hovered
          ? { rotate: 0, y: -16 }
          : { rotate: lean, y: 0 }
      }
      transition={{ type: "spring", stiffness: 280, damping: 22 }}
      whileTap={!failed ? { scale: 0.97 } : undefined}
      className="relative shrink-0 cursor-pointer"
      style={{
        background: "transparent",
        border: "none",
        padding: 0,
        outline: "none",
        cursor: failed ? "default" : "pointer",
        transformOrigin: "bottom center",
        scrollSnapAlign: "start",
      }}
      aria-label={`Open ${title}`}
    >
      {/* The book — front cover with thumbnail + title */}
      <div
        className="relative overflow-hidden"
        style={{
          width: "clamp(120px, 14vw, 160px)",
          height: "clamp(180px, 21vw, 240px)",
          background: "#FFFFFF",
          border: "2px solid rgba(11,26,51,0.10)",
          borderRadius: 12,
          // Two-side shadow: makes it feel 3D and rooted on the shelf.
          boxShadow: hovered
            ? "0px 14px 24px rgba(11,26,51,0.22), 0px 5px 0px rgba(11,26,51,0.10)"
            : "0px 8px 14px rgba(11,26,51,0.14), 0px 3px 0px rgba(11,26,51,0.10)",
          opacity: failed ? 0.6 : 1,
          transition: "box-shadow 220ms ease",
        }}
      >
        {/* Cover thumbnail */}
        <img
          src={thumb}
          alt={title}
          className="w-full h-full object-cover"
          loading="lazy"
          onError={(e) => {
            e.target.src = "/assets/story-placeholder.png";
          }}
          style={{ filter: processing ? "blur(2px) brightness(0.85)" : "none" }}
        />

        {/* Subtle vertical highlight — book "page" glint along the right side */}
        <div
          aria-hidden="true"
          style={{
            position: "absolute",
            top: 0,
            bottom: 0,
            right: 0,
            width: 4,
            background:
              "linear-gradient(to left, rgba(255,255,255,0.45), rgba(255,255,255,0) 100%)",
            pointerEvents: "none",
          }}
        />
        {/* Subtle vertical shadow — book "spine" along the left side */}
        <div
          aria-hidden="true"
          style={{
            position: "absolute",
            top: 0,
            bottom: 0,
            left: 0,
            width: 6,
            background:
              "linear-gradient(to right, rgba(11,26,51,0.18), rgba(11,26,51,0) 100%)",
            pointerEvents: "none",
          }}
        />

        {/* Title bar at the bottom */}
        <div
          className="absolute inset-x-0 bottom-0 px-2.5 py-2"
          style={{
            background:
              "linear-gradient(to top, rgba(11,26,51,0.85) 0%, rgba(11,26,51,0.55) 50%, transparent 100%)",
          }}
        >
          <p
            style={{
              ...FREDOKA,
              color: "#FFFFFF",
              fontWeight: 700,
              fontSize: 13,
              letterSpacing: "-0.01em",
              lineHeight: 1.15,
              margin: 0,
              display: "-webkit-box",
              WebkitLineClamp: 2,
              WebkitBoxOrient: "vertical",
              overflow: "hidden",
            }}
          >
            {title}
          </p>
        </div>

        {/* Processing overlay — small ring + % */}
        {processing && (
          <div
            className="absolute inset-0 flex flex-col items-center justify-center"
            style={{ background: "rgba(11,26,51,0.42)" }}
          >
            <div
              style={{
                width: 36,
                height: 36,
                borderRadius: "50%",
                border: "3px solid rgba(255,255,255,0.25)",
                borderTopColor: "#FFFFFF",
                animation: "lb-book-spin 1s linear infinite",
              }}
            />
            {percent > 0 && (
              <span
                style={{
                  ...FREDOKA,
                  marginTop: 6,
                  color: "#FFFFFF",
                  fontWeight: 800,
                  fontSize: 11,
                }}
              >
                {percent}%
              </span>
            )}
          </div>
        )}

        {/* Failed badge */}
        {failed && (
          <div
            className="absolute top-2 left-2 px-2 py-0.5"
            style={{
              background: "#DC2626",
              color: "#FFFFFF",
              borderRadius: 999,
              fontFamily: "'Fredoka', sans-serif",
              fontWeight: 800,
              fontSize: 10,
              letterSpacing: "0.06em",
              textTransform: "uppercase",
            }}
          >
            Failed
          </div>
        )}

        {/* AI Voice Storybook badge */}
        {isAiVoice && (
          <div
            className="absolute top-2 right-2 inline-flex items-center gap-1 px-2 py-0.5"
            style={{
              background: "linear-gradient(135deg, #0065F8 0%, #00CAFF 100%)",
              color: "#FFFFFF",
              borderRadius: 999,
              fontFamily: "'Fredoka', sans-serif",
              fontWeight: 800,
              fontSize: 10,
              letterSpacing: "0.06em",
              boxShadow: "0px 2px 0px rgba(11,26,51,0.18)",
            }}
          >
            <svg width="9" height="9" fill="currentColor" viewBox="0 0 24 24">
              <path d="M12 2l1.6 5.2L19 9l-5.4 1.8L12 16l-1.6-5.2L5 9l5.4-1.8L12 2z" />
            </svg>
            AI
          </div>
        )}
      </div>
    </motion.button>
  );
};

// One wooden shelf plank — used as a row separator and footer.
const ShelfPlank = () => (
  <div
    aria-hidden="true"
    style={{
      position: "relative",
      height: 26,
      borderRadius: 10,
      background:
        "linear-gradient(180deg, #C99564 0%, #B07F4A 60%, #8E5F30 100%)",
      boxShadow:
        "0px 6px 0px rgba(11,26,51,0.10), 0px 14px 24px rgba(11,26,51,0.14)",
    }}
  >
    <svg
      width="100%"
      height="100%"
      viewBox="0 0 600 26"
      preserveAspectRatio="none"
      style={{ position: "absolute", inset: 0 }}
    >
      <path
        d="M0 8 Q120 12 240 8 T480 9 T600 8"
        stroke="#8E5F30"
        strokeWidth="0.6"
        fill="none"
        opacity="0.4"
      />
      <path
        d="M0 18 Q150 22 320 17 T600 19"
        stroke="#5C3D1F"
        strokeWidth="0.5"
        fill="none"
        opacity="0.45"
      />
    </svg>
  </div>
);

// Books have a 120px floor, so a fixed 5-up row overflows narrow screens.
// Shelve fewer books per row instead of letting them collide.
const booksPerPageFor = (width) => {
  if (width < 480) return 2;
  if (width < 768) return 3;
  if (width < 1024) return 4;
  return 5;
};

const useBooksPerPage = () => {
  const [perPage, setPerPage] = useState(() =>
    typeof window === "undefined" ? 5 : booksPerPageFor(window.innerWidth)
  );

  useEffect(() => {
    const onResize = () => setPerPage(booksPerPageFor(window.innerWidth));
    onResize();
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  return perPage;
};

const chunk = (arr, n) => {
  const out = [];
  for (let i = 0; i < arr.length; i += n) out.push(arr.slice(i, i + n));
  return out;
};

/**
 * Horizontal wooden shelf with picture books standing on it.
 *
 * - Collapsed (default): one row of up to 5 books visible. Overflow scrolls
 *   horizontally with **snap every 5 stories** (each "page" is a full-width
 *   snap target).
 * - Expanded: stacks multiple shelves vertically — each holds 5 books on a
 *   wooden plank, with the next shelf below it.
 */
const StoryShelf = ({
  stories = [],
  expanded = false,
  onStoryClick,
  onCreate,
  emptyTitle = "Your shelf is empty",
  emptySubtitle = "Create a story to fill it with adventures.",
  emptyCtaLabel = "+ Create your first story",
}) => {
  const perPage = useBooksPerPage();
  const empty = stories.length === 0;

  // ── Empty ──
  if (empty) {
    return (
      <div style={{ position: "relative", width: "100%", paddingTop: 24 }}>
        <div
          className="flex flex-col items-center w-full py-6"
          style={{ ...FREDOKA }}
        >
          <p style={{ color: "#0B1A33", fontWeight: 700, fontSize: 16, margin: "0 0 4px" }}>
            {emptyTitle}
          </p>
          <p style={{ color: "#5A6B85", fontSize: 13, margin: "0 0 14px" }}>
            {emptySubtitle}
          </p>
          <button
            type="button"
            onClick={() => onCreate?.()}
            className="px-5 py-2.5 transition-all active:scale-[0.98]"
            style={{
              ...FREDOKA,
              background: "#0065F8",
              color: "#FFFFFF",
              fontWeight: 700,
              fontSize: 14,
              border: "none",
              borderRadius: 999,
              boxShadow:
                "0px 4px 0px #073B8E, 0px 12px 24px rgba(0,101,248,0.20)",
              cursor: "pointer",
            }}
          >
            {emptyCtaLabel}
          </button>
        </div>
        <ShelfPlank />
        <style>{shelfKeyframes}</style>
      </div>
    );
  }

  // Render one row of (up to perPage) books — used in both modes.
  const renderBookRow = (rowStories, offsetIndex = 0) => (
    <div
      style={{
        display: "grid",
        gridTemplateColumns: `repeat(${perPage}, minmax(0, 1fr))`,
        gap: perPage <= 2 ? 12 : 18,
        alignItems: "end",
        justifyItems: "center",
        padding: perPage <= 2 ? "16px 12px 0" : "16px 24px 0",
      }}
    >
      {rowStories.map((story, i) => (
        <PictureBook
          key={story.id || `story-${offsetIndex + i}`}
          story={story}
          index={offsetIndex + i}
          onClick={onStoryClick}
        />
      ))}
      {/* Pad empty slots so books don't stretch on the last row/page */}
      {Array.from({ length: Math.max(0, perPage - rowStories.length) }).map((_, i) => (
        <div key={`pad-${i}`} aria-hidden="true" />
      ))}
    </div>
  );

  // ── Expanded: stacked shelves ──
  if (expanded) {
    const rows = chunk(stories, perPage);
    return (
      <div style={{ position: "relative", width: "100%", paddingTop: 24 }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
          {rows.map((row, rIdx) => (
            <div key={`shelf-${rIdx}`}>
              {renderBookRow(row, rIdx * perPage)}
              <div style={{ marginTop: -2 }}>
                <ShelfPlank />
              </div>
            </div>
          ))}
        </div>
        <style>{shelfKeyframes}</style>
      </div>
    );
  }

  // ── Collapsed: horizontal scroller snapping one shelf-width at a time ──
  return <CollapsedShelf stories={stories} perPage={perPage} renderBookRow={renderBookRow} />;
};

const CollapsedShelf = ({ stories, perPage, renderBookRow }) => {
  const pages = chunk(stories, perPage);
  const scrollerRef = useRef(null);
  const [activePage, setActivePage] = useState(0);

  const handleScroll = (e) => {
    const el = e.currentTarget;
    const idx = Math.round(el.scrollLeft / el.clientWidth);
    if (idx !== activePage) setActivePage(idx);
  };

  const scrollToPage = (idx) => {
    const el = scrollerRef.current;
    if (!el) return;
    el.scrollTo({ left: idx * el.clientWidth, behavior: "smooth" });
  };

  return (
    <div style={{ position: "relative", width: "100%", paddingTop: 24 }}>
      <div
        ref={scrollerRef}
        onScroll={handleScroll}
        className="cs-shelf-scroller"
        style={{
          display: "flex",
          overflowX: pages.length > 1 ? "auto" : "visible",
          scrollSnapType: "x mandatory",
          scrollBehavior: "smooth",
          scrollbarWidth: "none",
          msOverflowStyle: "none",
        }}
      >
        {pages.map((page, pIdx) => (
          <div
            key={`page-${pIdx}`}
            style={{
              flex: "0 0 100%",
              minWidth: "100%",
              scrollSnapAlign: "start",
              scrollSnapStop: "always",
            }}
          >
            {renderBookRow(page, pIdx * perPage)}
          </div>
        ))}
      </div>
      <ShelfPlank />

      {/* Page indicators */}
      {pages.length > 1 && (
        <div className="mt-5 flex items-center justify-center gap-2">
          {pages.map((_, idx) => (
            <button
              key={`shelf-dot-${idx}`}
              type="button"
              onClick={() => scrollToPage(idx)}
              aria-label={`Go to shelf page ${idx + 1}`}
              className="h-2.5 rounded-full transition-all"
              style={
                idx === activePage
                  ? { width: 28, background: "#0065F8", boxShadow: "0px 2px 0px #073B8E", border: "none", cursor: "pointer" }
                  : { width: 10, background: "rgba(11,26,51,0.15)", border: "none", cursor: "pointer" }
              }
            />
          ))}
        </div>
      )}

      <style>{shelfKeyframes}</style>
    </div>
  );
};

const shelfKeyframes = `
  .cs-shelf-scroller::-webkit-scrollbar { display: none; }
  @keyframes lb-book-spin {
    to { transform: rotate(360deg); }
  }
`;

export default StoryShelf;
export { PictureBook };
